import { useState, useEffect, useMemo } from 'react';
import { createInitialFormState, validateForm } from '../utils/form';

export function useForm({ initialFields, loadDropdowns, loadRules, gender }) {
  // Form state
  const [formData, setFormData] = useState(() =>
    initialFields ? createInitialFormState(initialFields) : {}
  );

  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  // Async data
  const [dropdowns, setDropdowns] = useState({});
  const [rules, setRules] = useState(null);

  // Loading & error for form state (dropdowns / rules)
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(null);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Load dropdowns and rules
  useEffect(() => {
    async function load() {
      try {
        const promises = [];
        if (loadDropdowns) promises.push(loadDropdowns({gender}));
        if (loadRules) promises.push(loadRules({gender}));

        const results = await Promise.all(promises);

        if (loadDropdowns) {
          const dropdownData = results[0];
          const constants =
            (dropdownData && dropdownData.constants) || dropdownData || {};
          setDropdowns(constants);
        }

        if (loadRules) {
          const rulesData = loadDropdowns ? results[1] : results[0];
          setRules(rulesData);
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to load form data:', err);
        setErrors(err);
        setLoading(false);
      }
    }

    load();
  }, [loadDropdowns, loadRules, gender]);

  // Field-level validation 
  useEffect(() => {
    if (!rules) return;

    const validationErrors = validateForm({ formData, rules });
    setFieldErrors(validationErrors);
  }, [formData, rules]);


  /* -------------------------
     Derived form state
  --------------------------*/

  // Are all required fields filled?
  const isComplete = useMemo(() => {
    if (!initialFields) return false;

    return initialFields.every(field => {
      if (!field.required) return true;

      const value = formData[field.name];
      return (
        value !== undefined &&
        value !== null &&
        value.toString().trim() !== ''
      );
    });
  }, [formData, initialFields]);

  // Does the form pass validation rules?
  const isValid = useMemo(() => {
    return Object.keys(fieldErrors).length === 0;
  }, [fieldErrors]);

  // console.log("Dropdowns state in useForm:", dropdowns); // debug

  return {
    formData,
    handleChange,
    dropdowns,
    rules,
    loading,
    errors,
    fieldErrors,   // 👈 ADD THIS
    setFormData,
    isComplete,
    isValid
  };

}
